import { getIdObject, onIdObj, onVersionedObj } from '@refinio/one.core/lib/storage-versioned-objects.js';
import { ensureVersionedObjectTypeName } from '@refinio/one.core/lib/object-recipes.js';
import { onUnversionedObj } from '@refinio/one.core/lib/storage-unversioned-objects.js';
import { SettingsStore } from '@refinio/one.core/lib/system/settings-store.js';
import { enableStatistics, getStatistics, resetStatistics } from '@refinio/one.core/lib/util/object-io-statistics.js';
import { ensureIdHash } from '@refinio/one.core/lib/util/type-checks.js';
import BlockingPriorityQueue from './BlockingPriorityQueue.js';
import { getOrCreate } from '../utils/MapUtils.js';
import { OEvent } from './OEvent.js';
import { createMessageBus } from '@refinio/one.core/lib/message-bus.js';
const MessageBus = createMessageBus('ObjectEventDispatcher');
/**
 * The object event dispatcher collects all object events from one.core and reemits them in a
 * controlled fashion.
 *
 * - calls are serialized
 * - the interface is much more puwerful than that of one.core
 * - statistics make it much easier to debug stuff
 */
export default class ObjectEventDispatcher {
    onError = new OEvent();
    onGlobalStatisticChanged = new OEvent();
    onPauseStateChanged = new OEvent();
    /**
     * This option discards objects for which nobody listens before they are pushed to the buffer.
     *
     * This might have the drawback that if an object in the buffer causes a new event listener
     * to be registered, the new event listener will miss such objects. This might not be a
     * problem at the moment, because such objects will already be on disk (That's why it is
     * enabled by default).
     */
    enableEnqueueFiltering = true;
    /**
     * The application can override the priority values for results that are enqueued by setting
     * a function here.
     *
     * Lesser values will result in a higher priority.
     *
     * @param result - The result value that shall be enqueued.
     */
    determinePriorityOverride;
    // ######## private properties ########
    /**
     * Buffer that buffers all one.core events.
     * @private
     */
    buffer = new BlockingPriorityQueue(Number.POSITIVE_INFINITY, 1);
    bufferHistory = [];
    // #### event handler ####
    newVersionHandler = new Map();
    newUnversionedObjectHandler = new Map();
    newIdHandler = new Map();
    // #### Members for stopping / pausing the event loop ####
    stopped = true;
    waitForEventLoopDonePromise = null;
    disconnect;
    pausePromise;
    pauseResume;
    // Statistics of old / disconnected handlers
    oldVersionHandler = new Map();
    oldUnversionedObjectHandler = new Map();
    oldIdHandler = new Map();
    totalExecutionCount = 0;
    // ######## init / shutdown ########
    async init() {
        if (this.disconnect !== undefined) {
            throw new Error('ObjectEventDispatcher is already initialized.');
        }
        const d1 = onVersionedObj.addListener((result) => {
            return this.appendToBufferIfNew(result);
        });
        const d2 = onUnversionedObj.addListener(this.appendToBufferIfNew.bind(this));
        const d3 = onIdObj.addListener(result => {
            getIdObject(result.idHash)
                .then(obj => {
                const idObjResult = {
                    obj,
                    idHash: result.idHash,
                    status: result.status
                };
                this.appendToBufferIfNew(idObjResult);
            })
                .catch(this.onError.emit.bind(this.onError));
        });
        this.disconnect = () => {
            d1();
            d2();
            d3();
        };
        this.startDispatchLoop().catch(this.onError.emit.bind(this.onError));
    }
    async shutdown() {
        if (this.disconnect === undefined) {
            return;
        }
        this.disconnect();
        this.disconnect = undefined;
        this.stopped = true;
        this.resume();
        this.buffer.cancelPendingPromises();
        if (this.waitForEventLoopDonePromise) {
            await this.waitForEventLoopDonePromise;
        }
    }
    /**
     * Returns true if init() has been called and shutdown() hasn't been called.
     */
    isInitialized() {
        return this.disconnect !== undefined;
    }
    // ######## start / pause event handling ########
    pause() {
        if (this.pausePromise) {
            throw new Error('Already paused');
        }
        this.pausePromise = new Promise(resolve => {
            this.pauseResume = resolve;
            this.onPauseStateChanged.emit(true);
        });
    }
    resume() {
        if (this.pauseResume) {
            this.pausePromise = undefined;
            this.onPauseStateChanged.emit(false);
            this.pauseResume();
        }
    }
    isPaused() {
        return this.pausePromise !== undefined;
    }
    // ######## Event handler registration ########
    onNewVersion(cb, description, type = '*', idHash = '*') {
        const filterKey = idHash === '*' ? type : `${type}+${idHash}`;
        MessageBus.send('debug', `[OBJECT_EVENTS] onNewVersion - Registering handler: ${description}, filter: ${filterKey}`);
        const entry = getOrCreate(this.newVersionHandler, filterKey, []);
        entry.push({
            cb: cb,
            description,
            callStack: new Error('').stack,
            registerTime: Date.now(),
            executionStatistics: []
        });
        MessageBus.send('debug', `[OBJECT_EVENTS] onNewVersion - Handler registered: ${description}`);
        return () => {
            MessageBus.send('debug', `[OBJECT_EVENTS] onNewVersion - Handler deregistered: ${description}`);
            const i = entry.findIndex(h => h.cb === cb);
            if (i < 0) {
                return;
            }
            const oldHandlers = entry.splice(i, 1);
            if (this.retainDeregisteredHandlers) {
                const deregisterTime = Date.now();
                const oldEntry = getOrCreate(this.oldVersionHandler, type, []);
                for (const oldHandler of oldHandlers) {
                    oldHandler.deregisterTime = deregisterTime;
                }
                oldEntry.push(...oldHandlers);
            }
        };
    }
    onUnversionedObject(cb, description, type = '*') {
        const entry = getOrCreate(this.newUnversionedObjectHandler, type, []);
        entry.push({
            cb: cb,
            description,
            callStack: new Error('').stack,
            registerTime: Date.now(),
            executionStatistics: []
        });
        return () => {
            const i = entry.findIndex(h => h.cb === cb);
            if (i < 0) {
                return;
            }
            const oldHandlers = entry.splice(i, 1);
            if (this.retainDeregisteredHandlers) {
                const deregisterTime = Date.now();
                const oldEntry = getOrCreate(this.oldUnversionedObjectHandler, type, []);
                for (const oldHandler of oldHandlers) {
                    oldHandler.deregisterTime = deregisterTime;
                }
                oldEntry.push(...oldHandlers);
            }
        };
    }
    onNewIdObject(cb, description, type = '*') {
        const entry = getOrCreate(this.newIdHandler, type, []);
        entry.push({
            cb: cb,
            description,
            callStack: new Error('').stack,
            registerTime: Date.now(),
            executionStatistics: []
        });
        return () => {
            const i = entry.findIndex(h => h.cb === cb);
            if (i < 0) {
                return;
            }
            const oldHandlers = entry.splice(i, 1);
            if (this.retainDeregisteredHandlers) {
                const deregisterTime = Date.now();
                const oldEntry = getOrCreate(this.newIdHandler, type, []);
                for (const oldHandler of oldHandlers) {
                    oldHandler.deregisterTime = deregisterTime;
                }
                oldEntry.push(...oldHandlers);
            }
        };
    }
    static determinePriority(result) {
        if (result.obj.$type$ === 'Profile') {
            return 10;
        }
        return 0;
    }
    // ######## status & statistics ########
    // Global
    /**
     * If disabled no statistics except the global statistics are computed.
     *
     * Global statistics ar the global event counters.
     */
    enableStatistics = true;
    /**
     * If true, then I/O call statistics are recorded during handler execution.
     *
     * Note that this might accumulate a lot of data and reduce performance.
     */
    enableIOCallStatistics = false;
    // Statistic 1: Number of objects processed
    /**
     * Get the number of objects that were processed by the buffer.
     */
    get totalObjectCount() {
        return this.totalExecutionCount;
    }
    /**
     * Resets the total execution count.
     */
    resetTotalObjectCount() {
        this.totalExecutionCount = 0;
    }
    // Statistic 2: Pending objects
    /**
     * Get the number of objects that wait to be processed.
     */
    get pendingObjectCount() {
        return this.buffer.length;
    }
    /**
     * Get a list of pending objects (result type of objects, that includes hashes etc)
     *
     * Objects are deep frozen, the array is a copy.
     */
    getPendingObjects() {
        return this.buffer.data;
    }
    // Statistic 3: Processed objects
    /**
     * Number of processed object information to retain.
     *
     * -1 means unlimited, which will result in a serious memory leak.
     */
    maxProcessedObjectCount = 10;
    /**
     * Get a list of objects that have recently been processed plus statistics data for them.
     */
    getProcessedObjects() {
        return [...this.bufferHistory];
    }
    /**
     * Clears the whole buffer history.
     */
    clearProcessedObjects() {
        this.bufferHistory = [];
    }
    // Statistic 4: Statistics regarding registered handler
    /**
     * If true this will retain all handler that have been deregistered.
     *
     * This is useful to keep track of temporary callbacks (like the ones used in react views)
     */
    retainDeregisteredHandlers = false;
    /**
     * How many execution statistics per callback are stored.
     *
     * -1 means unlimited, which will result in a serious memory leak.
     */
    maxExecutionStatisticsPerHandler = 10;
    /**
     * Get a list of all registered handlers and if 'retainDeregisteredCallbacks' is enabled
     * also deregistered ones.
     */
    getHandlerStatistics() {
        const arr = [];
        for (const [key, handler] of [
            ...this.newVersionHandler.entries(),
            ...this.oldVersionHandler.entries()
        ]) {
            let filter;
            const elems = key.split('+');
            if (elems.length === 2) {
                filter = {
                    filterType: ensureVersionedObjectTypeName(elems[0]),
                    filterIdHash: ensureIdHash(elems[1])
                };
            }
            else if (elems.length === 1) {
                if (elems[0] === '*') {
                    filter = {
                        filterType: '*',
                        filterIdHash: '*'
                    };
                }
                else {
                    filter = {
                        filterType: ensureVersionedObjectTypeName(elems[0]),
                        filterIdHash: '*'
                    };
                }
            }
            else {
                throw new Error('Internal formatting error (1)');
            }
            for (const h of handler) {
                arr.push({
                    type: 'onNewVersion',
                    ...filter,
                    ...h
                });
            }
        }
        for (const [key, handler] of [
            ...this.newUnversionedObjectHandler.entries(),
            ...this.oldUnversionedObjectHandler.entries()
        ]) {
            for (const h of handler) {
                arr.push({
                    type: 'onUnversionedObject',
                    filterType: key,
                    ...h
                });
            }
        }
        for (const [key, handler] of [
            ...this.newIdHandler.entries(),
            ...this.oldIdHandler.entries()
        ]) {
            for (const h of handler) {
                arr.push({
                    type: 'onIdObject',
                    filterType: key,
                    ...h
                });
            }
        }
        return arr;
    }
    /**
     * Clear all execution statistics of handlers
     */
    clearHandlerStatistics() {
        for (const [_key, handler] of [
            ...this.newVersionHandler.entries(),
            ...this.oldVersionHandler.entries(),
            ...this.newUnversionedObjectHandler.entries(),
            ...this.oldUnversionedObjectHandler.entries(),
            ...this.newIdHandler.entries(),
            ...this.oldIdHandler.entries()
        ]) {
            for (const h of handler) {
                h.executionStatistics = [];
            }
        }
    }
    // ######## Store / Load settings ########
    /**
     * Load all settings from localStorage.
     *
     * If no settings are present - use sensible defaults.
     */
    async loadSettings() {
        const lvalue = await SettingsStore.getItem('objectEventDispatcherStatisticsSettings');
        if (lvalue === undefined) {
            return;
        }
        if (typeof lvalue !== 'string') {
            throw new Error('loadSettings: Malformed objectEventDispatcherStatisticsSettings in local storage');
        }
        const settingsObj = JSON.parse(lvalue);
        this.enableStatistics = settingsObj.enableStatistics;
        this.enableIOCallStatistics = settingsObj.enableIOCallStatistics;
        this.maxProcessedObjectCount = settingsObj.maxProcessedObjectCount;
        this.retainDeregisteredHandlers = settingsObj.retainDeregisteredHandlers;
        this.maxExecutionStatisticsPerHandler = settingsObj.maxExecutionStatisticsPerHandler;
    }
    /**
     * Store settings in localStorage.
     */
    async storeSettings() {
        await SettingsStore.setItem('objectEventDispatcherStatisticsSettings', JSON.stringify({
            enableStatistics: this.enableStatistics,
            enableIOCallStatistics: this.enableIOCallStatistics,
            maxProcessedObjectCount: this.maxProcessedObjectCount,
            retainDeregisteredHandlers: this.retainDeregisteredHandlers,
            maxExecutionStatisticsPerHandler: this.maxExecutionStatisticsPerHandler
        }));
    }
    // #### Private stuff ####
    // private reportError(error: any): void {
    //     if (this.onError.listenerCount() > 0) {
    //         this.onError.emit(error);
    //     } else {
    //         console.error('ObjectEventDispatcher: Error during event processing', error);
    //     }
    // }
    appendToBufferIfNew(result) {
        if (result.status === 'exists') {
            return;
        }
        const handlers = this.getHandler(result);
        if (this.enableEnqueueFiltering && handlers.length === 0) {
            // CRITICAL: Never filter out ChannelInfo objects - they're essential for message sync
            // ChannelInfo events must always be queued because:
            // 1. CHUM may import them before ChannelManager registers its handler
            // 2. Dropping them breaks message sync between paired devices
            // 3. They can be re-processed later when handler is available
            const isChannelInfo = 'obj' in result && result.obj?.$type$ === 'ChannelInfo';
            if (!isChannelInfo) {
                return;
            }
            MessageBus.send('debug', '[OBJECT_EVENTS] Queuing ChannelInfo despite no handlers (critical for sync)');
        }
        deepFreeze(result);
        const priority = this.determinePriorityOverride === undefined
            ? ObjectEventDispatcher.determinePriority(result)
            : this.determinePriorityOverride(result);
        this.buffer.add(result, priority);
        this.onGlobalStatisticChanged.emit();
    }
    async markAsDone(_result) {
        // TODO: remove it from disk
    }
    async dispatchHandler(result, handler) {
        if (this.enableStatistics) {
            const executedHandlerList = [];
            const startTime = Date.now();
            for (const h of handler) {
                let error;
                if (this.enableIOCallStatistics) {
                    enableStatistics(true, 'ObjectEventDispatcher');
                }
                const handlerStartTime = Date.now();
                try {
                    await h.cb(result);
                }
                catch (e) {
                    error = e;
                    MessageBus.send('debug', `[OBJECT_EVENTS] Handler failed: ${h.description} - ${e instanceof Error ? e.message : String(e)}`);
                }
                const handlerEndTime = Date.now();
                let ioCallStatistics;
                if (this.enableIOCallStatistics) {
                    enableStatistics(false, 'ObjectEventDispatcher');
                    ioCallStatistics = getStatistics('ObjectEventDispatcher');
                    resetStatistics('ObjectEventDispatcher');
                }
                executedHandlerList.push({
                    info: h,
                    startTime: handlerStartTime,
                    endTime: handlerEndTime,
                    error,
                    ioCallStatistics
                });
                this.pushHandlerExecutionStatistics(h, {
                    startTime: handlerStartTime,
                    endTime: handlerEndTime,
                    idHash: result.idHash || undefined,
                    hash: result.hash || undefined,
                    error,
                    ioCallStatistics
                });
                if (error !== undefined) {
                    this.onError.emit(error);
                }
            }
            const endTime = Date.now();
            this.pushBufferHistoryData({
                result,
                handler: executedHandlerList,
                startTime,
                endTime
            });
        }
        else {
            for (const h of handler) {
                try {
                    await h.cb(result);
                }
                catch (e) {
                    MessageBus.send('debug', `[OBJECT_EVENTS] Handler failed: ${h.description} - ${e instanceof Error ? e.message : String(e)}`);
                    this.onError.emit(e);
                }
            }
        }
    }
    async startDispatchLoop() {
        let resolvePromise = () => {
            // noop
        };
        this.waitForEventLoopDonePromise = new Promise(resolve => {
            resolvePromise = resolve;
        });
        this.stopped = false;
        for (;;) {
            let result;
            try {
                result = await this.buffer.remove();
                if (this.pausePromise) {
                    await this.pausePromise;
                }
                if (this.stopped) {
                    resolvePromise();
                    break;
                }
            }
            catch (e) {
                MessageBus.send('debug', `[OBJECT_EVENTS] Error in event loop: ${e instanceof Error ? e.message : String(e)}`);
                resolvePromise();
                break;
            }
            const handlers = this.getHandler(result);
            await this.dispatchHandler(result, handlers);
            await this.markAsDone(result);
            ++this.totalExecutionCount;
            this.onGlobalStatisticChanged.emit();
            if (this.stopped) {
                resolvePromise();
                break;
            }
        }
    }
    /**
     * Get handler that are registered for this result.
     *
     * Note: Somehow the as casts are needed, because typescript does not recognize, that a
     * VersionedObjectResult always leads to an Array of HandlerInfo<VersionedObjectResult> and
     * so on for unversioned and Id ...
     * That is why the 'as' casts are needed.
     *
     * This is public by intention. The ui needs it to visualize handlers for pending objects.
     *
     * @param result
     */
    getHandler(result) {
        if (isVersionedResult(result)) {
            return [
                ...(this.newVersionHandler.get(result.obj.$type$) || []),
                ...(this.newVersionHandler.get(`${result.obj.$type$}+${result.idHash}`) || []),
                ...(this.newVersionHandler.get('*') || [])
            ];
        }
        else if (isUnversionedResult(result)) {
            return [
                ...(this.newUnversionedObjectHandler.get(result.obj.$type$) || []),
                ...(this.newUnversionedObjectHandler.get('*') || [])
            ];
        }
        else {
            return [
                ...(this.newIdHandler.get(result.obj.$type$) || []),
                ...(this.newIdHandler.get('*') || [])
            ];
        }
    }
    pushBufferHistoryData(data) {
        if (isVersionedResult(data.result)) {
            this.bufferHistory.push({
                type: 'VersionedObject',
                ...data
            });
        }
        else if (isUnversionedResult(data.result)) {
            this.bufferHistory.push({
                type: 'UnversionedObject',
                ...data
            });
        }
        else {
            this.bufferHistory.push({
                type: 'IdObject',
                ...data
            });
        }
        trimArray(this.bufferHistory, this.maxProcessedObjectCount);
    }
    pushHandlerExecutionStatistics(h, statistics) {
        h.executionStatistics.push(statistics);
        trimArray(h.executionStatistics, this.maxExecutionStatisticsPerHandler);
    }
}
// ######## Utils ########
function deepFreeze(object) {
    // Retrieve the property names defined on object
    const propNames = Reflect.ownKeys(object);
    // Freeze properties before freezing self
    for (const name of propNames) {
        const value = object[name];
        if ((value && typeof value === 'object') || typeof value === 'function') {
            deepFreeze(value);
        }
    }
    return Object.freeze(object);
}
function isVersionedResult(result) {
    if (!Object.hasOwn(result, 'idHash')) {
        return false;
    }
    return Object.hasOwn(result, 'timestamp');
}
function isUnversionedResult(result) {
    return Object.hasOwn(result, 'hash');
}
function trimArray(arr, maxSize) {
    if (maxSize > -1 && arr.length > maxSize) {
        arr.splice(0, arr.length - maxSize);
    }
}
// TODO Temporary global, until we adjust the architecture
export const objectEvents = new ObjectEventDispatcher();
//# sourceMappingURL=ObjectEventDispatcher.js.map